/**
 * Laravel Echo + Pusher (Soketi) wrapper. Singleton — one connection per
 * authenticated session. We intentionally lazy-create the Echo instance only
 * when a caller asks to subscribe to a channel; if the env vars aren't
 * configured (e.g. in tests or a tenant without Soketi yet), every call
 * returns null and the rest of the app falls back to polling.
 *
 * Auth model: the /broadcasting/auth endpoint on Laravel is gated by
 * `auth:api` (Passport bearer). Echo signs each private-channel subscription
 * request with the current bearer token. If the token rotates (login,
 * impersonation), `disconnectEcho()` tears the instance down so the next
 * subscribe picks up the new token.
 */
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { getActiveBearerToken, url as backendBaseUrl } from '../utils/apiCaller';

// Echo internally calls `new window.Pusher(...)`. We attach the imported
// Pusher class to window so it can find it without the consumer having to
// do this dance in every entry point.
(window as any).Pusher = Pusher;

let echoInstance: Echo<any> | null = null;

function buildEcho(): Echo<any> | null {
    const key = process.env.REACT_APP_PUSHER_KEY;
    const host = process.env.REACT_APP_PUSHER_HOST;
    const portRaw = process.env.REACT_APP_PUSHER_PORT;
    const scheme = process.env.REACT_APP_PUSHER_SCHEME || 'http';
    const cluster = process.env.REACT_APP_PUSHER_CLUSTER || 'mt1';

    if (!key || !host || !portRaw) {
        // Misconfigured (or this is the login screen with no env loaded yet).
        // Falling through to polling is the right behavior — don't throw.
        return null;
    }

    const port = parseInt(portRaw, 10);
    if (Number.isNaN(port)) return null;

    const token = getActiveBearerToken();
    if (!token) {
        // Private channels need an auth token to sign with — we'll lazy-connect
        // again once the user is logged in.
        return null;
    }

    return new Echo({
        broadcaster: 'pusher',
        key,
        cluster,
        wsHost: host,
        wsPort: port,
        wssPort: port,
        forceTLS: scheme === 'https',
        encrypted: scheme === 'https',
        disableStats: true,
        enabledTransports: ['ws', 'wss'],
        authEndpoint: backendBaseUrl + 'broadcasting/auth',
        auth: {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            },
        },
    } as any);
}

export function getEcho(): Echo<any> | null {
    if (echoInstance) return echoInstance;
    echoInstance = buildEcho();
    return echoInstance;
}

export function disconnectEcho(): void {
    if (echoInstance) {
        try { echoInstance.disconnect(); } catch { /* noop */ }
        echoInstance = null;
    }
}

/**
 * Subscribe to a user's private notification channel. Returns an unsubscribe
 * function suitable for a `useEffect` cleanup. Returns null when Echo isn't
 * configured — callers must handle the null case (their feature still works
 * via polling).
 */
export function subscribeToUserNotifications(
    userId: number,
    onEvent: (payload: any) => void,
): (() => void) | null {
    const echo = getEcho();
    if (!echo) return null;

    const channelName = `notifications.user.${userId}`;
    try {
        const channel = echo.private(channelName);
        // Leading dot tells Echo to use the `broadcastAs` name verbatim
        // instead of prefixing with the PHP namespace.
        channel.listen('.notification.created', onEvent);

        return () => {
            try {
                channel.stopListening('.notification.created');
                echo.leave(channelName);
            } catch {
                // Channel may already be torn down; swallow.
            }
        };
    } catch {
        return null;
    }
}
